import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import PageHeader from "../../components/PageHeader";
import { trash } from "../../asset";
import { useBranchCode } from "../../util/hooks/useBranch";
import { useEffect, useState } from "react";
import { Notice } from "../../types/noticeBoardType";
import noticeBoardApis from "../../api/noticeBoardApis";
import { formatDateTime } from "../../util/functions/UniversalFunctions";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { toast } from "sonner";

export default function NoticeBoard() {
  const navigate = useNavigate();

  const branchCode = useBranchCode();

  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const { confirm, modal } = useConfirmToast();

  useEffect(() => {
    if (branchCode) fetchNotices();
  }, [branchCode]);

  const handleDelete = async (noticeId: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this notice?",
    );

    if (!confirmed) return;

    try {
      await noticeBoardApis.deleteNoticeBoard(noticeId);

      setNotices((prev) =>
        prev.filter((notice) => notice.noticeId !== noticeId),
      );
      toast.success("Notice deleted successfully");
    } catch (error) {
      // console.error("Delete failed", error);
      // toast.error("Failed to delete notice");
    }
  };

  const fetchNotices = async () => {
    setLoading(true);

    const res = await noticeBoardApis.getNotices(branchCode);

    if (res?.data) {
      setNotices(res.data);
    }

    setLoading(false);
  };

  const today = new Date();

  const activeNotice = notices.find(
    (n) => new Date(n.activeFrom) <= today && new Date(n.activeTo) >= today,
  );

  const previousNotices = notices.filter((n) => new Date(n.activeTo) < today);

  if (!loading && notices.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <PageHeader />
          <CustomButton
            color="bg-primary-500"
            text="Create Notice/Alerts"
            onClick={() => navigate("/notice-board/create-new-notice")}
          />
        </div>

        <div className="bg-white rounded-lg py-16 flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center">
            <span className="text-2xl">📢</span>
          </div>

          <h3 className="text-lg font-semibold text-neutral-700">
            No Notices Yet
          </h3>

          <p className="text-sm text-neutral-400 text-center max-w-sm">
            There are currently no notices or alerts for this branch. Create a
            new notice to inform staff or students.
          </p>

          <CustomButton
            color="bg-primary-500"
            text="Create First Notice"
            onClick={() => navigate("/notice-board/create-new-notice")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <PageHeader />
        <CustomButton
          color="bg-primary-500"
          text="Create Notice/Alerts"
          onClick={() => navigate("/notice-board/create-new-notice")}
        />
      </div>

      {!loading && activeNotice && (
        <div className="bg-white rounded-lg px-4 md:px-6 py-5 flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-error-500 w-2 h-2"></span>
              <span className="text-neutral-400 text-sm font-semibold">
                Currently active notice
              </span>
            </div>
            <img
              src={trash}
              alt="icon"
              className="w-5 h-5 cursor-pointer"
              onClick={() => handleDelete(activeNotice.noticeId)}
            />
          </div>
          <div className="border border-neutral-100 rounded px-4 p-2 flex justify-between items-center">
            <p className="text-sm max-w-4xl text-neutral-400">
              {activeNotice?.notice}
            </p>
            <CustomButton
              color="bg-warning-700"
              text="Update"
              onClick={() =>
                navigate("/notice-board/create-new-notice", {
                  state: { notice: activeNotice },
                })
              }
            />
          </div>
        </div>
      )}

      <section className="bg-white rounded-lg mt-2 px-4 md:px-6 py-5 flex flex-col gap-3">
        <h2 className="text-neutral-400 text-sm font-semibold">
          Previous Notices
        </h2>

        {previousNotices?.map((notice) => (
          <article
            key={notice.noticeId}
            className="border border-neutral-100 rounded px-4 p-2 flex flex-col gap-2"
          >
            <header>
              <p className="text-warning-700 text-sm font-medium">
                Running Window: <time>{formatDateTime(notice.activeFrom)}</time>{" "}
                - <time>{formatDateTime(notice.activeTo)}</time>
              </p>
            </header>

            <p className="text-sm max-w-4xl text-neutral-400">
              {notice.notice}
            </p>
          </article>
        ))}
      </section>
      {modal}
    </div>
  );
}
